import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ClientUpdateApi, {
  ClientUpdateKind,
  ClientUpdateRecipient,
} from '../../api/ClientUpdateApi';

/**
 * Send a client a short update without leaving the page (Lloyd, 2 Oct 2026).
 *
 * The case update is drafted for staff to read and edit; nothing goes until Send is pressed. Email
 * only for now: texts wait on the carrier approving the firm's texting campaign.
 */

interface SendUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Preselected on a client or case page. Without it, the dialog opens with a client search. */
  client?: { guid: string; name: string } | null;
}

const SendUpdateDialog: React.FC<SendUpdateDialogProps> = ({ open, onClose, client }) => {
  const [recipient, setRecipient] = useState<ClientUpdateRecipient | null>(null);
  const [options, setOptions] = useState<ClientUpdateRecipient[]>([]);
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');
  const [kind, setKind] = useState<ClientUpdateKind>('portal');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientEmail, setRecipientEmail] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const clientGuid = client?.guid;
  const clientName = client?.name;

  // Keyed on the guid and name, not the object, so a parent re-render cannot wipe a draft mid-edit.
  useEffect(() => {
    if (!open) return;
    setRecipient(
      clientGuid ? { User_guid: clientGuid, name: clientName ?? '', email: null, phone: null } : null,
    );
    setOptions([]);
    setTerm('');
    setKind('portal');
    setSubject('');
    setBody('');
    setRecipientEmail(null);
    setError(null);
    setSentTo(null);
  }, [open, clientGuid, clientName]);

  // Each keystroke searches; only the newest search may fill the list, or a slow earlier reply
  // would replace the results for what was typed after it.
  const latestSearch = useRef(0);

  const search = useCallback(async (value: string): Promise<void> => {
    setTerm(value);
    const searchId = ++latestSearch.current;
    if (value.trim().length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const found = await ClientUpdateApi.search(value.trim());
      if (searchId === latestSearch.current) setOptions(found);
    } catch {
      if (searchId === latestSearch.current) setOptions([]);
    } finally {
      if (searchId === latestSearch.current) setSearching(false);
    }
  }, []);

  const draft = async (): Promise<void> => {
    if (!recipient) return;
    setDrafting(true);
    setError(null);
    try {
      const drafted = await ClientUpdateApi.draft(recipient.User_guid, kind);
      setSubject(drafted.subject);
      setBody(drafted.body);
      setRecipientEmail(drafted.recipientEmail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to draft the update');
    } finally {
      setDrafting(false);
    }
  };

  const send = async (): Promise<void> => {
    if (!recipient) return;
    setSending(true);
    setError(null);
    try {
      await ClientUpdateApi.send(recipient.User_guid, subject.trim(), body.trim());
      setSentTo(recipientEmail ?? recipient.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the update');
    } finally {
      setSending(false);
    }
  };

  const chooseClient = (value: ClientUpdateRecipient | null): void => {
    setRecipient(value);
    setSubject('');
    setBody('');
    setRecipientEmail(null);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send update</DialogTitle>
      <DialogContent>
        {sentTo ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Sent to {sentTo}. It is also in the client's messages.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {client ? (
              <Typography>
                <strong>Client:</strong> {client.name}
              </Typography>
            ) : (
              <Autocomplete
                size="small"
                options={options}
                loading={searching}
                value={recipient}
                filterOptions={(x) => x}
                getOptionLabel={(option) =>
                  [option.name, option.email, option.phone].filter(Boolean).join(' · ')
                }
                isOptionEqualToValue={(a, b) => a.User_guid === b.User_guid}
                noOptionsText={term.trim().length < 2 ? 'Type a name, email or phone number' : 'No clients found'}
                onChange={(_event, value) => chooseClient(value)}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') void search(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Name, email or phone"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={kind}
                onChange={(_event, value: ClientUpdateKind | null) => {
                  if (!value) return;
                  setKind(value);
                  // The message belonged to the other choice, so it would mislead about this one.
                  setError(null);
                }}
              >
                <ToggleButton value="portal">Portal message</ToggleButton>
                <ToggleButton value="ai">Case update (AI draft)</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" onClick={draft} disabled={!recipient || drafting}>
                {drafting ? 'Drafting...' : 'Draft'}
              </Button>
            </Stack>

            {(subject || body) && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {recipientEmail
                    ? `To: ${recipientEmail}`
                    : 'This client has no email address on file, so it cannot be sent.'}
                </Typography>
                <TextField
                  label="Subject"
                  size="small"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
                <TextField
                  label="Message"
                  multiline
                  minRows={5}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  inputProps={{ maxLength: 5000 }}
                  helperText="Read and edit before sending. Sent by email; texts will follow once texting is approved."
                />
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{sentTo ? 'Close' : 'Cancel'}</Button>
        {!sentTo && (
          <Button
            variant="contained"
            onClick={send}
            disabled={!recipient || !recipientEmail || !subject.trim() || !body.trim() || sending}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendUpdateDialog;
